import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";

import { faPlus, faXmark } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components/Icon/Icon";
import { classNames } from "@/utils/classNames";

import { AiSnippetEnum } from "@/enum/ai-enrichment.enum";

interface ResponseTypeRadiosProps {
	promptType: AiSnippetEnum;
	allowedValues: string[];
	onChangePromptType: (type: AiSnippetEnum) => void;
	onChangeAllowedValues: (values: string[]) => void;
}

export const ResponseTypeRadios: FunctionComponent<ResponseTypeRadiosProps> = ({
	promptType,
	allowedValues,
	onChangePromptType,
	onChangeAllowedValues,
}) => {
	const { t } = useTranslation();

	const isBest = promptType === AiSnippetEnum.BEST_RESPONSE;
	const isSchema = promptType === AiSnippetEnum.SCHEMA;

	const handleAdd = () => {
		onChangeAllowedValues([...allowedValues, ""]);
	};

	const handleUpdate = (index: number, value: string) => {
		const next = allowedValues.slice();

		next[index] = value;
		onChangeAllowedValues(next);
	};

	const handleRemove = (index: number) => {
		onChangeAllowedValues(allowedValues.filter((_, i) => i !== index));
	};

	const renderRadio = (
		type: AiSnippetEnum,
		selected: boolean,
		label: string,
		helper: string,
		nestedBody?: React.ReactNode
	) => (
		<button
			className={classNames(
				"flex flex-col w-full text-left rounded-lg border bg-white p-3 transition-all",
				selected
					? "border-blue-action bg-blue-action-200 shadow-[0_0_0_2px_rgba(29,61,242,0.08)]"
					: "border-border-light hover:border-gray-300"
			)}
			role="radio"
			type="button"
			aria-checked={selected}
			onClick={() => onChangePromptType(type)}
		>
			<div className="flex flex-row items-start gap-2.5 w-full">
				<span
					className={classNames(
						"mt-0.5 w-4 h-4 rounded-full border-2 flex-shrink-0 relative",
						selected ? "border-blue-action" : "border-gray-300"
					)}
				>
					{selected && <span className="absolute inset-[2px] rounded-full bg-blue-action block" />}
				</span>
				<div className="flex-1">
					<div className="text-sm font-semibold text-text leading-tight">{label}</div>
					<div className="text-xs text-text-meta mt-0.5 leading-relaxed">{helper}</div>
				</div>
			</div>
			{selected && nestedBody ? (
				<div className="mt-2.5 pt-2.5 border-t border-dashed border-border-light w-full">
					{nestedBody}
				</div>
			) : null}
		</button>
	);

	const valuesBody = (
		<div className="flex flex-col gap-1.5">
			<div className="flex flex-col gap-1.5">
				{allowedValues.map((value, index) => (
					<div
						key={index}
						className="flex flex-row items-stretch border border-border-light rounded-md bg-white overflow-hidden focus-within:border-blue-action focus-within:shadow-[0_0_0_3px_rgba(29,61,242,0.08)]"
						onClick={(e) => e.stopPropagation()}
					>
						<input
							aria-label={t("aiSnippet.modal.responseType.schema.valuePlaceholder")}
							className="flex-1 border-0 outline-none px-2.5 py-1.5 text-xs bg-transparent text-text min-w-0"
							placeholder={t("aiSnippet.modal.responseType.schema.valuePlaceholder")}
							value={value}
							onChange={(e) => handleUpdate(index, e.target.value)}
						/>
						<button
							aria-label={t("aiSnippet.modal.responseType.schema.removeValue")}
							className="flex-shrink-0 border-0 bg-transparent px-2 cursor-pointer text-text-meta hover:text-status-error hover:bg-status-error-light flex items-center justify-center border-l border-transparent"
							type="button"
							onClick={(e) => {
								e.stopPropagation();
								handleRemove(index);
							}}
						>
							<Icon className="w-3 h-3" icon={faXmark} />
						</button>
					</div>
				))}
			</div>
			<button
				className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 bg-transparent border border-dashed border-gray-300 rounded-md text-text-meta text-xs font-medium hover:border-blue-action hover:text-blue-action hover:bg-blue-action-200 hover:border-solid transition-all"
				type="button"
				onClick={(e) => {
					e.stopPropagation();
					handleAdd();
				}}
			>
				<Icon className="w-3 h-3" icon={faPlus} />
				{t("aiSnippet.modal.responseType.schema.addValue")}
			</button>
		</div>
	);

	return (
		<div className="flex flex-col gap-2" role="radiogroup">
			{renderRadio(
				AiSnippetEnum.BEST_RESPONSE,
				isBest,
				t("aiSnippet.modal.responseType.best.label"),
				t("aiSnippet.modal.responseType.best.helper")
			)}
			{renderRadio(
				AiSnippetEnum.SCHEMA,
				isSchema,
				t("aiSnippet.modal.responseType.schema.label"),
				t("aiSnippet.modal.responseType.schema.helper"),
				valuesBody
			)}
		</div>
	);
};
